import React from 'react';
import { useParams } from 'react-router-dom';
import {
  IconCalendar, IconClock, IconMapPin, IconNotes, IconCalendarPlus, IconNavigation, IconPhone,
} from '@tabler/icons-react';
import TopBar from '../../components/layout/TopBar';
import {
  Avatar, Button, ErrorState, Skeleton, StatusBadge,
} from '../../components/common';
import CancelSheet from '../../components/booking/CancelSheet';
import appointmentApi from '../../api/appointment/appointmentApi';
import useFetch from '../../hooks/useFetch';
import useCancelAppointment from './useCancelAppointment';
import { useClinic } from '../../context/ClinicContext';
import { openDirections } from '../../services/mapsService';
import { downloadIcs } from '../../services/calendarService';
import { PATHS, toPath } from '../../routes/paths';
import { formatFullDate, formatTime, dayLong, fromDateKey } from '../../utils/date';
import { formatCurrency } from '../../utils/format';
import { UPCOMING_STATUSES } from '../../utils/constants';

export default function AppointmentDetailPage() {
  const { id } = useParams();
  const { clinic } = useClinic();
  const { data: a, loading, error, reload, setData } = useFetch(() => appointmentApi.get(id), [id]);
  const cancel = useCancelAppointment((updated) => setData(updated));

  const upcoming = a && UPCOMING_STATUSES.includes(a.status);

  return (
    <div className={`page ${upcoming ? 'page--with-footer-lg' : ''} appt-detail`}>
      <TopBar title="Appointment" bordered />
      <div className="page__body u-stack-4">
        {loading && !a && <Skeleton count={3} height={120} />}
        {error && <ErrorState error={error} onRetry={reload} />}
        {a && (
          <>
            {a.status === 'pending' && (
              <div className="alert alert--warning">
                The clinic will confirm this appointment shortly. We’ll notify you by SMS.
              </div>
            )}
            <section className="card card--pad">
              <div className="u-flex u-items-center u-gap-3">
                <Avatar name={a.doctor?.name} />
                <div className="u-grow">
                  <p className="appt-detail__name">{a.doctor?.name}</p>
                  <p className="u-muted">{[a.doctor?.specialty, a.visitType].filter(Boolean).join(' · ')}</p>
                </div>
              </div>
              <div className="u-mt-4"><StatusBadge status={a.status} /></div>
            </section>

            <section className="list-card">
              <div className="list-row">
                <span className="icon-tile"><IconCalendar size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">{formatFullDate(a.date)}</p>
                  <p className="list-row__sub">{dayLong(fromDateKey(a.date))}</p>
                </div>
              </div>
              <div className="list-row">
                <span className="icon-tile"><IconClock size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">{formatTime(a.time)}</p>
                  <p className="list-row__sub">{`${a.duration || 30} min · In-clinic visit`}</p>
                </div>
              </div>
              <div className="list-row">
                <span className="icon-tile"><IconMapPin size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">{clinic?.name}</p>
                  <p className="list-row__sub">{clinic?.address}</p>
                </div>
              </div>
              {a.reason && (
                <div className="list-row">
                  <span className="icon-tile"><IconNotes size={20} /></span>
                  <div className="list-row__body">
                    <p className="list-row__title">Reason for visit</p>
                    <p className="list-row__sub">{a.reason}</p>
                  </div>
                </div>
              )}
              {a.status === 'cancelled' && a.cancelReason && (
                <div className="list-row">
                  <span className="icon-tile"><IconNotes size={20} /></span>
                  <div className="list-row__body">
                    <p className="list-row__title">Cancellation reason</p>
                    <p className="list-row__sub">{a.cancelReason}</p>
                  </div>
                </div>
              )}
            </section>

            <section className="card card--pad-sm u-flex u-justify-between u-items-center">
              <span className="u-muted">Consultation fee</span>
              <strong>{formatCurrency(a.fee, clinic?.currency)}</strong>
            </section>

            <div className="action-grid">
              {upcoming && (
                <button type="button" className="action-grid__item" onClick={() => downloadIcs({ appointment: a, clinic })}>
                  <IconCalendarPlus size={22} />
                  Add to calendar
                </button>
              )}
              <button type="button" className="action-grid__item" onClick={() => openDirections(clinic)}>
                <IconNavigation size={22} />
                Directions
              </button>
              {clinic && (
                <a className="action-grid__item" href={`tel:${clinic.phone.countryCode}${clinic.phone.number}`}>
                  <IconPhone size={22} />
                  Call clinic
                </a>
              )}
            </div>
            {!upcoming && a.doctor && (
              <Button block variant="outline" to={toPath(PATHS.book, { doctorId: a.doctor.id })}>Book again</Button>
            )}
          </>
        )}
      </div>

      {upcoming && (
        <div className="sticky-footer">
          <p className="u-muted u-text-sm u-mb-2 u-text-center">
            {`Changes can be made up to ${clinic?.cancellationCutoffHours || 2} hours before your visit.`}
          </p>
          <div className="appt-card__actions" style={{ marginTop: 0 }}>
            <Button variant="danger-outline" onClick={() => cancel.open(a)}>Cancel</Button>
            <Button to={toPath(PATHS.reschedule, { id: a.id })}>Reschedule</Button>
          </div>
        </div>
      )}
      <CancelSheet appointment={cancel.target} onClose={cancel.close} onConfirm={cancel.confirm} loading={cancel.loading} />
    </div>
  );
}
